/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import ReactDOM from 'react-dom';
import 'watson-react-components/dist/css/watson-react-components.css';
import { Provider } from 'react-redux';
import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { logger } from 'redux-logger';
import rootReducer from 'store/reducers';
import App from './app';
import './style.scss';
import { loadCustomStringPrototypes } from './string';
import HttpInterceptor from './services/HttpInterseptor.js';
import AnalyticsService from './services/AnalyticsService.js';
import { LocalSettingsService } from './services/LocalSettingsService';
import { H } from 'highlight.run';
import { ErrorBoundary } from '@highlight-run/react';

// ============================================================================
// Error & Warning Suppression
// ============================================================================
// Suppress benign errors and warnings that are known issues from libraries
// or browser behavior that don't affect functionality

// 1. ResizeObserver Loop Error Suppression
// This is a harmless browser warning that occurs when ResizeObserver can't 
// deliver all notifications in a single animation frame (common with charts/dynamic content)
const resizeObserverErr = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  if (message && message.toString().includes('ResizeObserver loop')) {
    return true; // Suppress this error
  }
  return resizeObserverErr ? resizeObserverErr(message, source, lineno, colno, error) : false;
};

// Suppress via error event listener for React dev overlay
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('ResizeObserver loop')) {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
  }
}, true); // Use capture phase

// Suppress unhandledrejection for ResizeObserver
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver loop')) {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
  }
}, true);

// 2. Console Warning Suppression
// Filter out known library warnings that we cannot fix (from react-router-dom v4, react-ga, etc.)
const originalConsoleWarn = console.warn;
const originalConsoleError = console.error;

console.warn = (...args) => {
  const message = args[0]?.toString() || '';
  
  // Suppress react-router-dom v4 deprecated lifecycle warnings
  if (message.includes('componentWillMount') || 
      message.includes('componentWillReceiveProps') ||
      message.includes('componentWillUpdate')) {
    return;
  }
  
  originalConsoleWarn.apply(console, args);
};

console.error = (...args) => {
  const message = args[0]?.toString() || '';
  
  // Suppress ResizeObserver errors in console
  if (message.includes('ResizeObserver loop')) {
    return;
  }
  
  // Suppress Google Analytics initialization warnings
  if (message.includes('ReactGA.initialize must be called first') ||
      message.includes('GoogleAnalytics should be loaded manually')) {
    return;
  }
  
  // Suppress WebSocket connection errors (webpack-dev-server HMR)
  if (message.includes('WebSocket connection') && message.includes('failed')) {
    return;
  }
  
  originalConsoleError.apply(console, args);
};

if (process.env.REACT_APP_DEPLOYMENT_NAME) {
  // Parse network recording blocklist from environment variable
  const networkBlocklist = process.env.REACT_APP_NETWORK_BLOCKLIST 
    ? process.env.REACT_APP_NETWORK_BLOCKLIST.split(',').map(url => url.trim())
    : [];

  H.init(process.env.REACT_APP_HIGHLIGHT_PROJECT_ID, {
    serviceName: process.env.REACT_APP_DEPLOYMENT_NAME,
    tracingOrigins: true,
    networkRecording: {
      enabled: true,
      recordHeadersAndBody: true,
      urlBlocklist: networkBlocklist,
    },
  });
}

const middlewares = [thunk];
// Keep logger opt-in to avoid noisy console output in local development.
const enableReduxLogger = process.env.REACT_APP_ENABLE_REDUX_LOGGER === 'true';
if (
  process.env.NODE_ENV === 'development'
  && !process.env.REACT_APP_DEPLOYMENT_NAME
  && enableReduxLogger
) {
  middlewares.push(logger);
}

const store = createStore(
  rootReducer,
  applyMiddleware(...middlewares),
);

loadCustomStringPrototypes();
new HttpInterceptor().load();
new LocalSettingsService().initLocalSettings();
window.tooltipDatas = {};
window.profileDatas = {};
window.tooltipColors = {};

// Initialize Analytics before rendering to ensure it's ready for pageviews
AnalyticsService.init();

ReactDOM.render(
  <Provider store={store}>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </Provider>,
  document.getElementById('root')
);
